import { EDeliveryOption } from '@/services/types/core/delivery.types';

export enum EGuidedSearchComponentType {
  ButtonGroupRadio = 'button_group_radio',
  AddressInput = 'address_input',
  DateRangePicker = 'date_range_picker',
  NumericInput = 'numeric_input',
  // Adults + children steppers (and an optional pet-friendly toggle) that open a
  // shared overlay, replacing the separate numeric guest inputs and pet step.
  GuestOccupancy = 'guest_occupancy',
}

export interface IDeliveryInfo {
  address?: string;
  deliveryStationary?: EDeliveryOption;
  stationaryAcknowledged?: boolean;
  mapboxId?: string;
  deliveryCenter?: string;
  deliveryDetails?: string;
}

export interface IVehicleInfo {
  year?: number;
  make?: string;
  modelId?: number;
  towingCapacity?: number;
}

export interface ITripDistanceInfo {
  estimatedTripDistance?: number;
}

type QueryParam = {
  key: string;
  // Static value for button_group_radio rows. Omitted for location/dates/guests
  // fields, whose value is supplied positionally by the form's stored input.
  value?: string;
};

type TGuidedSearchComponentFieldRow = {
  id: string;
  label: string;
  query_params: QueryParam[];
};

// A single guest stepper (adults or children) inside a guest_occupancy field.
export type TGuestCountConfig = {
  label?: string;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  // The search-query key this count contributes, e.g. `sleeps[adults]`.
  queryKey: string;
};

// Configuration for a guest_occupancy field. Adults/children render as steppers;
// pet renders as a toggle that contributes `queryKey=queryValue` only when on.
export type TGuestOccupancyConfig = {
  adults?: TGuestCountConfig;
  children?: TGuestCountConfig;
  pet?: { queryKey: string; queryValue: string };
};

export type TGuidedSearchComponentField = {
  id: string;
  component_type: EGuidedSearchComponentType;
  // When true, the field does not require a selection before the step can advance.
  // Supplied by the data file so required/optional is configurable per field rather
  // than inferred from the component type. Falls back to the type heuristic when omitted.
  optional?: boolean;
  properties: {
    // Present for button_group_radio; omitted for address/dates/numeric fields.
    rows?: TGuidedSearchComponentFieldRow[];
    title?: string;
    description?: string;
    // Field-level input label (e.g. the address_input's floating label). Lets the
    // input be reused dynamically with copy supplied by the JSON.
    label?: string;
    // Secondary helper copy (e.g. a numeric_input's "Age 13 or above").
    subtitle?: string;
    // numeric_input bounds + initial value, all JSON-driven.
    minValue?: number;
    maxValue?: number;
    defaultValue?: number;
    // The query-param keys an address/dates/numeric field contributes, matched
    // positionally to the value(s) the form stores in the step selection.
    query_params?: QueryParam[];
    // Present for guest_occupancy fields — adults/children stepper bounds and the
    // optional pet toggle, all JSON-driven.
    guests?: TGuestOccupancyConfig;
  };
};

export type TGuidedSearchComponent = {
  id: string;
  fields: TGuidedSearchComponentField[];
  properties: {
    title: string;
  };
};

// The guided-search screens, supplied by the caller (bundled default data for
// the locality flow, a static asset for HelpMeSearch).
export type TGuidedSearchResponse = {
  data_file: {
    components: TGuidedSearchComponent[];
  };
};

export type TDatePickerComponentFieldValue = {
  from?: Date;
  to?: Date;
  flexible_days?: number;
};
